import React, { useState, useEffect, useCallback, useRef } from 'react';
import mainTheme from '../ProjectSettings.module.scss';
import { Trans } from '@lingui/react/macro';
import { ValidationForm, ValidationSummary } from '@/components/forms/validation';
import { useAsyncState, useMergeState } from '@/utils/hooks';
import {
    ProjectFeeOverrideSettings,
    saveProjectFeeSet,
    getProjectFeeSetsWithReferenceCount,
    deleteProjectFeeSet,
} from '@/services/fees';
import ProjectFeeOverrideSettingsDialog from './ProjectFeeOverrideSettingsDialog';
import { ProjectFeeSet, OptionalId, ProjectFeeSetWithReferenceCount, TranslatableText } from '@/types';
import theme from './ProjectSettingsFees.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEdit, faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import ProjectFeeCreateDialog from './ProjectFeeCreateDialog';
import { FeeEditor } from '@/seller/components/fees';
import { Tooltip } from '@/components/Tooltip';
import { classnames } from '@/utils/html';
import { YesNoDialog } from '@/components/dialogs';
import { isEqual } from 'lodash-es';
import { HttpStatusCode } from '@/HttpStatusCodes';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, Button, ButtonBar, ConfirmButton } from '@ygtechnologies/rubus-ui';

interface ProjectSettingsFeesProps {
    projectId: number;
}

const ProjectSettingsFees: React.FC<ProjectSettingsFeesProps> = ({ projectId }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [showSaveDialog, setSaveShowDialog] = useState(false);
    const [selectedFeeSet, setSelectedFeeSet] = useState<ProjectFeeSet | null>(null);
    const [showCreateDialog, setShowCreateDialog] = useState(false);

    const [externalErrors, setExternalErrors] = useState<TranslatableText[]>([]);

    const [editingFeeSet, mergeEditingFeeSet, setEditingFeeSet] = useMergeState<OptionalId<ProjectFeeSet> | null>(null);

    const [feeSets, setFeeSets] = useAsyncState(() => getProjectFeeSetsWithReferenceCount(projectId), [projectId], null);

    const [changeToFeeSet, setChangeToFeeSet] = useState<ProjectFeeSet | null>(null);
    const [isEditingName, setIsEditingName] = useState(false);
    const [tempName, setTempName] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const reset = useCallback(() => {
        setEditingFeeSet(
            selectedFeeSet || {
                name: '',
                isDefault: false,
                fees: [],
            }
        );
    }, [selectedFeeSet, setEditingFeeSet]);

    useEffect(() => {
        if (feeSets && !selectedFeeSet) {
            setSelectedFeeSet(feeSets[0]);
        } else if (feeSets && selectedFeeSet) {
            setSelectedFeeSet(feeSets.find((x) => x.id === selectedFeeSet.id) || null);
        }
    }, [feeSets, selectedFeeSet]);

    useEffect(() => {
        reset();
    }, [reset]);

    const feeSetModified = selectedFeeSet && selectedFeeSet.fees !== editingFeeSet!.fees;

    const doSave = async (item: OptionalId<ProjectFeeSet>, overrideSettings: ProjectFeeOverrideSettings | null) => {
        setIsSaving(true);

        try {
            item.fees = item.fees.map((x) => ({
                ...x,
                taxPercentage: x.taxPercentage || 0,
                feePercentage: x.feePercentage || 0,
            }));

            var result = await saveProjectFeeSet(projectId, item, overrideSettings);

            setSelectedFeeSet(result);

            if (item.id) {
                const itemToReplace = feeSets!.find((x) => x.id === item.id)!;
                const replacement = { ...result, referenceCount: itemToReplace.referenceCount || 0 };
                setFeeSets(arrayReplace(feeSets!, itemToReplace, replacement));
            } else {
                setFeeSets([...feeSets!, { ...result, referenceCount: 0 }]);
            }

            Toast.success(<Trans>Saved fee and VAT configuration</Trans>);
        } catch (e: any) {
            if (e.status === HttpStatusCode.Conflict) {
                const errorMessage = <Trans>A fee set with this name already exists</Trans>;
                Toast.error(errorMessage);
                setExternalErrors([...externalErrors, errorMessage]);

                // reloadFeeSets();
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = () => {
        if (feeSetModified) setSaveShowDialog(true);
        else doSave(editingFeeSet!, {});
    };

    const handleDeleteFeeSet = async (id: number) => {
        await deleteProjectFeeSet(projectId, id);
        setFeeSets(feeSets!.filter((fs) => fs.id !== id));
        Toast.success(<Trans>Fee deleted</Trans>);
    };

    const handleChangeFeeSet = (feeSet: ProjectFeeSetWithReferenceCount) => {
        if (!isEqual(selectedFeeSet, editingFeeSet)) setChangeToFeeSet(feeSet);
        else setSelectedFeeSet(feeSet);
    };

    const handleEditName = () => {
        setIsEditingName(true);
        setTempName(editingFeeSet?.name || '');
    };

    const handleSaveName = () => {
        mergeEditingFeeSet({ name: tempName });
        setIsEditingName(false);
    };

    const handleCancelEdit = () => {
        setIsEditingName(false);
        setTempName(editingFeeSet?.name || '');
    };

    if (!feeSets) return null;

    const getTooltipContent = (fs: ProjectFeeSetWithReferenceCount) => {
        if (fs.isDefault) {
            return <Trans>You cannot delete the default fee</Trans>;
        }
        if (fs.referenceCount > 0) {
            return <Trans>This fee is in use and cannot be deleted</Trans>;
        }
        return null;
    };
    return (
        <>
            <AlertBanner type="info">
                <Trans id="fee-editor-info">
                    You can create multiple fee setups (e.g., "General" or "Kitchen"), and each setup can include several
                    fees (e.g., "Client", "Main contractor", or company names). To ensure fees are grouped correctly in Excel
                    exports, the fee name must be exactly the same across all setups.
                </Trans>
            </AlertBanner>
            <ValidationForm onSubmit={handleSubmit} className={mainTheme.section}>
                <div className={theme.container}>
                    <ul className={theme.list}>
                        {feeSets.map((fs) => (
                            <li key={fs.id}>
                                <span
                                    onClick={() => handleChangeFeeSet(fs)}
                                    className={classnames('text-truncate', { [theme.active]: fs === selectedFeeSet })}>
                                    {fs.name}&nbsp;
                                    {fs.isDefault && (
                                        <>
                                            (<Trans>default</Trans>)
                                        </>
                                    )}
                                </span>
                                <Tooltip interactive={false} content={getTooltipContent(fs)}>
                                    <span>
                                        <ConfirmButton
                                            onConfirm={() => handleDeleteFeeSet(fs.id)}
                                            dialogTitle={<Trans>Are you sure?</Trans>}
                                            dialogBody={<Trans>This will delete the fee. It cannot be undone.</Trans>}
                                            disabled={fs.referenceCount > 0 || fs.isDefault}
                                            variant="danger"
                                            size="xs"
                                            icon={faTrashAlt}
                                            confirmButton={{
                                                variant: 'danger',
                                                children: <Trans>Delete</Trans>,
                                            }}>
                                            {null}
                                        </ConfirmButton>
                                    </span>
                                </Tooltip>
                            </li>
                        ))}
                        <li className={theme.new} onClick={(e) => setShowCreateDialog(true)}>
                            <span>
                                <Trans>Create new fee type</Trans>
                            </span>
                            <FontAwesomeIcon className={theme.addIcon} icon={faPlus} />
                        </li>
                    </ul>
                    {editingFeeSet && (
                        <div>
                            <div className={theme.nameEditor}>
                                {isEditingName ? (
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={tempName}
                                        onChange={(e) => setTempName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleSaveName();
                                            } else if (e.key === 'Escape') {
                                                handleCancelEdit();
                                            }
                                        }}
                                        onBlur={handleSaveName}
                                        className={theme.nameEditorInput}
                                    />
                                ) : (
                                    <span className={theme.nameEditorDisplay}>{editingFeeSet.name}</span>
                                )}

                                <FontAwesomeIcon
                                    icon={isEditingName ? faCheck : faEdit}
                                    onClick={isEditingName ? handleSaveName : handleEditName}
                                    className={theme.editIcon}
                                />
                            </div>

                            <FeeEditor
                                key={editingFeeSet.id}
                                value={editingFeeSet.fees || []}
                                onChange={(data) => mergeEditingFeeSet({ fees: data })}
                            />
                        </div>
                    )}
                </div>
                <ValidationSummary externalErrors={externalErrors} />
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" disabled={isSaving} onClick={reset}>
                            <Trans>Reset</Trans>
                        </Button>
                        <Button className={mainTheme.saveButton} type="submit" variant="primary" loading={isSaving}>
                            <Trans>Save</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </ValidationForm>

            <ProjectFeeOverrideSettingsDialog
                show={showSaveDialog}
                onAccept={(data) => doSave(editingFeeSet!, data)}
                onClose={() => setSaveShowDialog(false)}
            />

            <ProjectFeeCreateDialog
                show={showCreateDialog}
                onClose={() => setShowCreateDialog(false)}
                onAccept={(data) => doSave(data, null)}
            />

            <YesNoDialog
                title={<Trans>Change feeset without saving</Trans>}
                body={
                    <>
                        <Trans>Are you sure you want to continue without saving?</Trans>
                        <AlertBanner className={theme.bannerMargin} type="info">
                            <Trans>
                                <strong>Please note</strong> if you change between feesets without saving, any unsaved
                                changes will be lost! To save any changes made, click 'Save changes'.
                            </Trans>
                        </AlertBanner>
                    </>
                }
                onAccept={() => setSelectedFeeSet(changeToFeeSet)}
                show={changeToFeeSet !== null}
                onClose={() => setChangeToFeeSet(null)}
                confirmButtonColor="tertiary"
                cancelButtonColor="secondary"
            />
        </>
    );
};

export default ProjectSettingsFees;
